import React, { useEffect, useRef } from "react"

const StickyScrollWrapper = ({
  children,
  className = "",
  contentClassName = "",
  boundarySelector,
  topOffset = 24,
  bottomOffset = 24,
  minWidth = 1024,
  disabled = false,
}) => {
  const wrapperRef = useRef(null)
  const contentRef = useRef(null)
  const rafRef = useRef(null)

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined
    }

    const wrapperEl = wrapperRef.current
    const contentEl = contentRef.current

    if (!wrapperEl || !contentEl) {
      return undefined
    }

    const clearInlineStyles = () => {
      wrapperEl.style.height = ""
      wrapperEl.style.position = ""

      contentEl.style.position = ""
      contentEl.style.top = ""
      contentEl.style.left = ""
      contentEl.style.width = ""
      contentEl.style.zIndex = ""
    }

    const getBoundaryEl = () => {
      if (!boundarySelector) {
        return wrapperEl.parentElement
      }
      return document.querySelector(boundarySelector)
    }

    const updateStickyPosition = () => {
      const boundaryEl = getBoundaryEl()

      if (
        disabled ||
        window.innerWidth < minWidth ||
        !boundaryEl ||
        !wrapperEl.isConnected ||
        !contentEl.isConnected
      ) {
        clearInlineStyles()
        return
      }

      const wrapperRect = wrapperEl.getBoundingClientRect()
      const boundaryRect = boundaryEl.getBoundingClientRect()
      const contentHeight = contentEl.offsetHeight

      // Sticky starts once wrapper top reaches top offset.
      if (wrapperRect.top > topOffset) {
        clearInlineStyles()
        return
      }

      const maxTopWithinBoundary = boundaryRect.bottom - bottomOffset - contentHeight

      wrapperEl.style.height = `${contentHeight}px`
      wrapperEl.style.position = "relative"

      contentEl.style.position = "fixed"
      contentEl.style.left = `${wrapperRect.left}px`
      contentEl.style.width = `${wrapperRect.width}px`
      contentEl.style.top = `${Math.min(topOffset, maxTopWithinBoundary)}px`
      contentEl.style.zIndex = "20"
    }

    const requestUpdate = () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current)
      }
      rafRef.current = window.requestAnimationFrame(updateStickyPosition)
    }

    const resizeObserver = new ResizeObserver(() => {
      requestUpdate()
    })

    resizeObserver.observe(contentEl)
    resizeObserver.observe(wrapperEl)

    const boundaryEl = getBoundaryEl()
    if (boundaryEl) {
      resizeObserver.observe(boundaryEl)
    }

    window.addEventListener("scroll", requestUpdate, { passive: true })
    window.addEventListener("resize", requestUpdate)

    requestUpdate()

    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current)
      }
      window.removeEventListener("scroll", requestUpdate)
      window.removeEventListener("resize", requestUpdate)
      resizeObserver.disconnect()
      clearInlineStyles()
    }
  }, [boundarySelector, topOffset, bottomOffset, minWidth, disabled])

  return (
    <div ref={wrapperRef} className={className}>
      <div ref={contentRef} className={contentClassName}>
        {children}
      </div>
    </div>
  )
}

export default StickyScrollWrapper
